import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { fmtDisplay } from "@/lib/fx";
import { FlipNumber } from "@/components/FlipNumber";
import { CountUp } from "@/components/CountUp";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowUpRight, ArrowDownRight, Wallet, SlidersHorizontal, Check } from "lucide-react";

const MODES = [
  { key: "holdings", label: "My holdings" },
  { key: "watchlist", label: "My watchlist" },
  { key: "both", label: "Holdings + watchlist" },
];

const Chg = ({ pct }) => {
  const up = Number(pct) >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`numeric text-[11px] inline-flex items-center gap-0.5 ${up ? "text-emerald-600" : "text-red-500"}`}>
      <Icon className="h-3 w-3" />{Math.abs(Number(pct) || 0).toFixed(2)}%
    </span>
  );
};

/** Balance, holdings and watchlist scrolling right to left. The client picks what shows. */
export const PortfolioTicker = ({ totalUsd }) => {
  const { displayCcy, user, refreshUser } = useApp();
  const [mode, setMode] = useState(user?.ticker_mode || "holdings");
  const [items, setItems] = useState([]);
  const [step, setStep] = useState(0);
  const [rolling, setRolling] = useState(true);

  const load = useCallback(async () => {
    const wants = { holdings: ["pos"], watchlist: ["wl"], both: ["pos", "wl"] }[mode] || ["pos"];
    const rows = [];
    if (wants.includes("pos")) {
      const { data } = await api.get("/positions");
      const bySymbol = new Map();
      data.forEach((p) => {
        const prev = bySymbol.get(p.symbol);
        if (prev) prev.value += p.market_value_usd;
        else bySymbol.set(p.symbol, {
          symbol: p.symbol, value: p.market_value_usd,
          chg: p.change_pct ?? p.pnl_pct, kind: "holding" });
      });
      rows.push(...[...bySymbol.values()].sort((a, b) => b.value - a.value).slice(0, 16));
    }
    if (wants.includes("wl")) {
      const { data } = await api.get("/watchlist");
      const held = new Set(rows.map((r) => r.symbol));
      rows.push(...data.filter((q) => !held.has(q.symbol)).slice(0, 12).map((q) => ({
        symbol: q.symbol, price: q.price, currency: q.currency,
        chg: q.change_pct, kind: "watch" })));
    }
    setItems(rows);
  }, [mode]);

  useEffect(() => {
    const onFail = (e) => console.error("Ticker refresh failed:", e);
    load().catch(onFail);
    const id = setInterval(() => load().catch(onFail), 12000);
    return () => clearInterval(id);
  }, [load]);

  // Roll one row up every couple of seconds; snap back invisibly after the duplicate first row.
  useEffect(() => {
    if (items.length < 2) return;
    const id = setInterval(() => setStep((s) => s + 1), 2600);
    return () => clearInterval(id);
  }, [items.length]);

  useEffect(() => {
    if (step !== items.length || !items.length) return;
    const id = setTimeout(() => { setRolling(false); setStep(0); }, 640);
    return () => clearTimeout(id);
  }, [step, items.length]);

  useEffect(() => {
    if (rolling) return;
    const id = setTimeout(() => setRolling(true), 60);
    return () => clearTimeout(id);
  }, [rolling]);

  const pick = async (next) => {
    setMode(next);
    try {
      await api.patch("/auth/preferences", { ticker_mode: next });
      refreshUser?.();
    } catch (e) { console.error("Couldn't save your ticker preference:", e); }
  };

  if (!items.length) return null;

  const balanceChip = (
    <span className="flex items-center gap-2 shrink-0 ps-4 pe-4 sm:pe-6 border-e border-border/60">
      <Wallet className="h-3.5 w-3.5 text-[color:var(--tharwy-lime-700)]" />
      <span className="hidden sm:inline text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        Total balance
      </span>
      <CountUp value={Number(totalUsd || 0)} testid="ticker-total-balance"
        format={(v) => fmtDisplay(v, displayCcy)} className="numeric text-sm font-semibold" />
    </span>
  );

  const row = (it) => (
    <span className="flex h-7 items-center gap-2">
      <span className="text-xs font-semibold">{it.symbol}</span>
      {it.kind === "watch" && (
        <span className="text-[9px] uppercase tracking-wider text-muted-foreground/70">watch</span>
      )}
      <FlipNumber value={Number((it.kind === "holding" ? it.value : it.price) || 0)}
        testid={`ticker-price-${it.symbol}`}
        format={(v) => (it.kind === "holding"
          ? fmtDisplay(v, displayCcy)
          : `${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${it.currency}`)}
        className="numeric text-xs text-muted-foreground" />
      <Chg pct={it.chg} />
    </span>
  );

  // One holding at a time, each rolling up out of the way for the next.
  const reel = [...items, items[0]];

  return (
    <div className="mb-4 flex items-center gap-1 rounded-xl border bg-card/70 overflow-hidden min-w-0"
      data-testid="dashboard-portfolio-ticker">
      {balanceChip}
      <div className="flex-1 min-w-0 h-7 overflow-hidden ps-4" data-testid="ticker-reel">
        <div style={{
          transform: `translateY(-${step * 1.75}rem)`,
          transition: rolling ? "transform 620ms cubic-bezier(0.22, 1, 0.36, 1)" : "none",
        }}>
          {reel.map((it, i) => (
            <div key={`${it.kind}-${it.symbol}-${i}`} className="h-7"
              data-testid={i === step ? "ticker-reel-current" : undefined}>
              {row(it)}
            </div>
          ))}
        </div>
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <button className="shrink-0 px-3 text-muted-foreground hover:text-foreground transition-colors"
            title="What the ticker shows" data-testid="ticker-settings">
            <SlidersHorizontal className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-52 p-1.5" data-testid="ticker-settings-menu">
          <div className="px-2 py-1.5 text-xs font-medium">Ticker shows</div>
          {MODES.map((m) => (
            <button key={m.key} onClick={() => pick(m.key)}
              data-testid={`ticker-mode-${m.key}`}
              className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-accent">
              {m.label}
              {mode === m.key && <Check className="h-3.5 w-3.5 text-primary" />}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
};
